import { Fragment, ReactNode, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";

import { clsxMerge } from "shared/lib/helpers";

import "swiper/css";
import "swiper/css/pagination";

type ItemInvariants<T> =
  | {
      items: T[];
    }
  | {
      mobileItems: T[];
      desktopItems: T[];
    };

type ResponsiveSliderGridPropsI<T> = {
  id?: string;
  className?: string;
  itemClassName?: string;
  renderItem: (item: T) => ReactNode;
  collapseToSliderOn?: "md" | "lg" | "xl" | "2xl";
} & ItemInvariants<T>;

function SliderMode<T>({
  id,
  className,
  itemClassName,
  renderItem,
  ...rest
}: ResponsiveSliderGridPropsI<T>) {
  const items = "items" in rest ? rest.items : rest.mobileItems;

  const paginationId = id ? `${id}-pagination` : "pagination";

  const slides = useMemo(
    () =>
      items.map((item, idx) => (
        <SwiperSlide
          key={idx}
          className={clsxMerge(
            "!flex !h-auto justify-center px-1 py-2",
            itemClassName
          )}
        >
          {renderItem(item)}
        </SwiperSlide>
      )),
    [items, renderItem, itemClassName]
  );

  return (
    <div className="relative w-full">
      <Swiper
        className={clsxMerge("w-full", className)}
        wrapperClass="w-full"
        modules={[Pagination]}
        pagination={{
          el: `#${paginationId}`,
          type: "bullets",
          bulletClass: "!w-3 !h-3 sw-bullet",
          bulletActiveClass: "sw-bullet-active !bg-black",
        }}
        cssMode
        initialSlide={1}
        spaceBetween={20}
        slidesPerView={1}
        breakpoints={{
          560: {
            slidesPerView: 2,
          },
        }}
      >
        {slides}
      </Swiper>

      <div
        id={paginationId}
        className="absolute !-bottom-8 flex justify-center"
      />
    </div>
  );
}

function GridMode<T>({
  className,
  renderItem,
  ...rest
}: ResponsiveSliderGridPropsI<T>) {
  const items = "items" in rest ? rest.items : rest.desktopItems;

  return (
    <div className={clsxMerge("flex justify-center gap-3", className)}>
      {items.map((item, idx) => (
        <Fragment key={idx}>{renderItem(item)}</Fragment>
      ))}
    </div>
  );
}

// This map represents the breakpoints at which we want to collapse grid into
// a slider
const RESPONSIVE_BREAKPOINTS = {
  md: ["md:block", "md:hidden"],
  lg: ["lg:block", "lg:hidden"],
  xl: ["xl:block", "xl:hidden"],
  "2xl": ["2xl:block", "2xl:hidden"],
};

// We often have cases when on tablet/desktop breakpoints some items like pricing
// tiers or other blog posts / customer success stories are displayed in a row,
// but on mobile we want to collapse them into a slider. This component solves
// this problem.
/**
 * @param className - class name of the container
 * @param itemClassName - class name of the rendered item
 * @param collapseToSliderOn - breakpoint on which grid collapses to slider
 *
 * NOTE - you can either provide items or both mobileItems and desktopItems
 * @param items - array of items to render
 * @param mobileItems - array of items to render on mobile
 * @param desktopItems - array of items to render on desktop
 * @param renderItem - render function of an item
 */
export function ResponsiveSliderGrid<T>({
  collapseToSliderOn = "lg",
  ...props
}: ResponsiveSliderGridPropsI<T>) {
  const [gridClassName, sliderClassName] =
    RESPONSIVE_BREAKPOINTS[collapseToSliderOn];

  return (
    <>
      <div className={clsxMerge("hidden w-full", gridClassName)}>
        <GridMode {...props} />
      </div>

      <div className={clsxMerge("block rounded-lg", sliderClassName)}>
        <SliderMode {...props} />
      </div>
    </>
  );
}
